/**
 * Admin Dashboard API — single aggregate endpoint.
 * One HTTP request returns all KPIs, health, queues, cron, growth,
 * sparkline cards, social pills, funnel, social trends, and alerts.
 */

import type { CronResult, HealthResult, QueueStats } from "./admin-system";
import { apiFetch } from "./client";

// ─── Shared Sub-types ───

export type UserStats = { readonly total: number };
export type BillingStats = { readonly total: number };
export type SummaryJobStats = { readonly total: number };

export type MessageBreakdown = {
  readonly human: number;
  readonly agent: number;
  readonly system: number;
};

// ─── v2.0 Dashboard Types ───

export type KpiCard = {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly sparkline_14d: readonly number[];
  readonly prev_value: number;
  readonly change_percent: number;
  readonly anomaly_level: "normal" | "warning" | "alert";
};

export type SocialPills = {
  readonly h2a_conversations: number;
  readonly h2h_conversations: number;
  readonly group_conversations: number;
  readonly agent_msg_percent: number;
  readonly paid_users: number;
  readonly total_agents: number;
};

export type FunnelStep = {
  readonly step_name: string;
  readonly step_order: number;
  readonly user_count: number;
};

export type SocialTrendDay = {
  readonly date: string;
  readonly h2a: number;
  readonly h2h: number;
  readonly group: number;
};

export type ActiveAlert = {
  readonly id: string;
  readonly severity: "critical" | "warning";
  readonly message: string;
  readonly detail: string;
  readonly link_to: string;
  readonly link_label: string;
};

// ─── Legacy KPIs (kept for backwards compat) ───

export type DashboardKPIs = {
  readonly users: UserStats;
  readonly paidUsers: BillingStats;
  readonly failedSummary24h: SummaryJobStats;
  readonly activeRelations: number;
  readonly totalConversations: number;
  readonly todayMessages: number;
  readonly messageBreakdown: MessageBreakdown;
  readonly health: HealthResult;
  readonly queues: QueueStats;
  readonly cron: CronResult;
};

// ─── Full Dashboard Data ───

export type DashboardData = {
  readonly kpis: DashboardKPIs;
  readonly growthRows: ReadonlyArray<{
    readonly metric_date: string;
    readonly metric_name: string;
    readonly metric_value: number;
  }>;
  readonly kpiCards: readonly KpiCard[];
  readonly socialPills: SocialPills;
  readonly funnelSummary: readonly FunnelStep[];
  readonly socialTrends: readonly SocialTrendDay[];
  readonly activeAlerts: readonly ActiveAlert[];
};

// ─── API Response Shape (snake_case from backend) ───

type DashboardApiResponse = {
  readonly users: UserStats;
  readonly paid_users: BillingStats;
  readonly failed_summary_24h: SummaryJobStats;
  readonly active_relations: number;
  readonly total_conversations: number;
  readonly today_messages: number;
  readonly message_breakdown: MessageBreakdown;
  readonly health: HealthResult;
  readonly queues: QueueStats;
  readonly cron: CronResult;
  readonly growth: ReadonlyArray<{
    readonly metric_date: string;
    readonly metric_name: string;
    readonly metric_value: number;
  }>;
  readonly kpi_cards: readonly KpiCard[];
  readonly social_pills: SocialPills;
  readonly funnel_summary: readonly FunnelStep[];
  readonly social_trends: readonly SocialTrendDay[];
  readonly active_alerts: readonly ActiveAlert[];
};

// ─── API Call ───

export async function fetchDashboardAll(): Promise<DashboardData> {
  const res = await apiFetch<{ data: DashboardApiResponse }>(
    "/admin/dashboard",
  );
  const d = res.data;
  return {
    kpis: {
      users: d.users,
      paidUsers: d.paid_users,
      failedSummary24h: d.failed_summary_24h,
      activeRelations: d.active_relations,
      totalConversations: d.total_conversations,
      todayMessages: d.today_messages,
      messageBreakdown: d.message_breakdown,
      health: d.health,
      queues: d.queues,
      cron: d.cron,
    },
    growthRows: d.growth,
    kpiCards: d.kpi_cards,
    socialPills: d.social_pills,
    funnelSummary: d.funnel_summary,
    socialTrends: d.social_trends,
    activeAlerts: d.active_alerts,
  };
}
